import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../components/css/Main.css";
import axios from "axios";
import Swal from "sweetalert2";


const formStyle = {
  maxWidth: "1500px",
  margin: "0px auto",
  background: "#fff",
  borderRadius: "0px",
  boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
  padding: "32px 24px",
  fontFamily: "'Sarabun', 'Prompt', 'Kanit', 'sans-serif'",
};

const labelStyle = {
  display: "block",
  marginBottom: "18px",
  color: "#222",
  fontWeight: 500,
  fontSize: "1rem",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  fontSize: "1rem",
  marginTop: "6px",
  background: "#fafbfc",
  outline: "none",
  boxSizing: "border-box",
  transition: "border 0.2s",
};

const textareaStyle = {
  ...inputStyle,
  minHeight: "80px",
  resize: "vertical",
};

const buttonStyle = {
  width: "100%",
  padding: "12px 0",
  background: "#1a73e8",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  fontSize: "1.1rem",
  fontWeight: 600,
  cursor: "pointer",
  marginTop: "0px",
  boxShadow: "0 1px 4px rgba(26,115,232,0.08)",
  transition: "background 0.2s",
};

const shakeAnimation = {
  animation: 'shake 0.5s ease-in-out',
};

// เพิ่ม CSS animation สำหรับการสั่น
const shakeKeyframes = `
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}
`;

// เพิ่ม CSS animation ลงใน document head
if (typeof document !== 'undefined' && !document.getElementById('shake-animation')) {
  const style = document.createElement('style');
  style.id = 'shake-animation';
  style.textContent = shakeKeyframes;
  document.head.appendChild(style);
}



function Form({ defaultPosition }) {
  const { t } = useTranslation();
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    email: "",
    position: defaultPosition || "",
    start: "",
    finish: "",
    phone: "",
    education: "",
    desc: "",
    extraFiles: [],
  });
  const handlePreview = (file) => {
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL, "_blank", "noopener,noreferrer");
  };
  const [resumeFile, setResumeFile] = useState(null);
  const [transcriptFile, setTranscriptFile] = useState(null);
  const [dragActiveResume, setDragActiveResume] = useState(false);
  const [dragActiveTranscript, setDragActiveTranscript] = useState(false);
  const [showResumeError, setShowResumeError] = useState(false);
  const [showTranscriptError, setShowTranscriptError] = useState(false);
  const [resumeErrorMessage, setResumeErrorMessage] = useState('');
  const [transcriptErrorMessage, setTranscriptErrorMessage] = useState('');
  const [shakeResume, setShakeResume] = useState(false);
  const [shakeTranscript, setShakeTranscript] = useState(false);
  const resumeInputRef = useRef(null);
  const transcriptInputRef = useRef(null);
  const [extraNote, setExtraNote] = useState("");

  // ฟอร์มสมัครเป็น public — ไม่ต้องขอ token (ฝั่ง Intern ลบ /public-token แล้ว)


  // ฟังก์ชันตรวจสอบประเภทไฟล์
  const isValidFileType = (file) => {
    const allowedTypes = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
    const fileName = file.name.toLowerCase();
    return allowedTypes.some(type => fileName.endsWith(type));
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Resume
  const handleResumeFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (isValidFileType(file)) {
        setResumeFile(file);
        setShowResumeError(false); // รีเซ็ตข้อผิดพลาดเมื่อเลือกไฟล์
        setResumeErrorMessage('');
        setShakeResume(false); // รีเซ็ตการสั่น
      } else {
        // แสดงข้อผิดพลาดเมื่อไฟล์ไม่ใช่ประเภทที่รองรับ
        setShowResumeError(true);
        setResumeErrorMessage(t('form.fileTypeError'));
        setShakeResume(true);
        setTimeout(() => setShakeResume(false), 500);
        // ล้างค่าใน input
        e.target.value = '';
      }
    }
  };
  const handleDragResume = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveResume(true);
    } else if (e.type === "dragleave") {
      setDragActiveResume(false);
    }
  };
  const handleDropResume = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveResume(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (isValidFileType(file)) {
        setResumeFile(file);
        setShowResumeError(false); // รีเซ็ตข้อผิดพลาดเมื่อเลือกไฟล์
        setResumeErrorMessage('');
        setShakeResume(false); // รีเซ็ตการสั่น
      } else {
        // แสดงข้อผิดพลาดเมื่อไฟล์ไม่ใช่ประเภทที่รองรับ
        setShowResumeError(true);
        setResumeErrorMessage(t('form.fileTypeError'));
        setShakeResume(true);
        setTimeout(() => setShakeResume(false), 500);
      }
    }
  };
  const handleResumeDropzoneClick = () => {
    resumeInputRef.current.click();
  };
  // Transcript
  const handleTranscriptFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (isValidFileType(file)) {
        setTranscriptFile(file);
        setShowTranscriptError(false); // รีเซ็ตข้อผิดพลาดเมื่อเลือกไฟล์
        setTranscriptErrorMessage('');
        setShakeTranscript(false); // รีเซ็ตการสั่น
      } else {
        // แสดงข้อผิดพลาดเมื่อไฟล์ไม่ใช่ประเภทที่รองรับ
        setShowTranscriptError(true);
        setTranscriptErrorMessage(t('form.fileTypeError'));
        setShakeTranscript(true);
        setTimeout(() => setShakeTranscript(false), 500);
        // ล้างค่าใน input
        e.target.value = '';
      }
    }
  };
  const handleDragTranscript = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveTranscript(true);
    } else if (e.type === "dragleave") {
      setDragActiveTranscript(false);
    }
  };
  const handleDropTranscript = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveTranscript(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (isValidFileType(file)) {
        setTranscriptFile(file);
        setShowTranscriptError(false); // รีเซ็ตข้อผิดพลาดเมื่อเลือกไฟล์
        setTranscriptErrorMessage('');
        setShakeTranscript(false); // รีเซ็ตการสั่น
      } else {
        // แสดงข้อผิดพลาดเมื่อไฟล์ไม่ใช่ประเภทที่รองรับ
        setShowTranscriptError(true);
        setTranscriptErrorMessage(t('form.fileTypeError'));
        setShakeTranscript(true);
        setTimeout(() => setShakeTranscript(false), 500);
      }
    }
  };
  const handleTranscriptDropzoneClick = () => {
    transcriptInputRef.current.click();
  };

  const API_KEY = process.env.REACT_APP_API_KEY;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ตรวจสอบไฟล์ที่จำเป็น
    let hasError = false;
    if (!resumeFile) {
      setShowResumeError(true);
      setResumeErrorMessage(t('form.missingResume'));
      setShakeResume(true);
      // รีเซ็ตการสั่นหลังจาก animation เสร็จ
      setTimeout(() => setShakeResume(false), 500);
      hasError = true;
    }
    if (!transcriptFile) {
      setShowTranscriptError(true);
      setTranscriptErrorMessage(t('form.missingTranscript'));
      setShakeTranscript(true);
      // รีเซ็ตการสั่นหลังจาก animation เสร็จ
      setTimeout(() => setShakeTranscript(false), 500);
      hasError = true;
    }

    // ถ้ามีข้อผิดพลาด ไม่ส่งฟอร์ม
    if (hasError) {
      return;
    }

    try {

      const confilm = await Swal.fire({
        title: t('form.confirmTitle'),
        text: t('form.confirmText'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: t('form.confirmYes'),
        cancelButtonText: t('form.cancel'),
      });
      if (!confilm.isConfirmed) {
        return;
      }

      const fd = new FormData();
      fd.append("name", formData.name);
      fd.append("surname", formData.surname);
      fd.append("email", formData.email);

      const pos =
        (typeof defaultPosition === "string" && defaultPosition) ||
        (typeof formData.position === "string"
          ? formData.position
          : (formData.position?.value || formData.position?.label || ""));

      fd.append("position", pos);

      if (formData.extraFiles && formData.extraFiles.length) {
        formData.extraFiles.forEach((f) => fd.append("extraFiles", f));
      }

      if (extraNote) {
        fd.append("extraNote", extraNote);
      }


      fd.append("start", formData.start);
      fd.append("finish", formData.finish);
      fd.append("phone", formData.phone);
      fd.append("education", formData.education);
      fd.append("desc", formData.desc || "");
      if (resumeFile) fd.append("resume", resumeFile);
      if (transcriptFile) fd.append("transcript", transcriptFile);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/internship-applicants/`, fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      Swal.fire({
        title: t('form.successTitle'),
        text: t('form.successText'),
        icon: "success",
        confirmButtonText: t('form.ok'),
      });

      if (formRef.current) {
        formRef.current.reset();
      }

      setFormData({
        name: "",
        surname: "",
        email: "",
        position: defaultPosition || "",
        start: "",
        finish: "",
        phone: "",
        education: "",
        desc: "",
        extraFiles: [],
      });

      setResumeFile(null);
      setTranscriptFile(null);
      setExtraNote("");
      setShowResumeError(false);
      setShowTranscriptError(false);

    } catch (error) {
      Swal.fire({
        title: t('form.errorTitle'),
        text: t('form.errorRateLimit'),
        icon: "error",
        confirmButtonText: t('form.ok'),
      });
    }


  };

  return (
    <div style={formStyle}>
      <form ref={formRef} onSubmit={handleSubmit} autoComplete="off">
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={labelStyle}>
              {t('form.firstName')}
              <input
                style={inputStyle}
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder={t('form.firstNamePh')}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={labelStyle}>
              {t('form.lastName')}
              <input
                style={inputStyle}
                type="text"
                name="surname"
                value={formData.surname}
                onChange={handleChange}
                required
                placeholder={t('form.lastNamePh')}
              />
            </label>
          </div>
        </div>
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={labelStyle}>
              {t('form.email')}
              <input
                style={inputStyle}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder={t('form.emailPh')}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={labelStyle}>
              {t('form.position')}
              <select
                style={inputStyle}
                name="position"
                value={formData.position}
                onChange={handleChange}
                required
                disabled={!!defaultPosition}
              >
                <option value="" disabled>{t('form.positionPh')}</option>
                <option value="IT Support">IT Support</option>
                <option value="Data Engineering">Data Engineering</option>
                <option value="Software Developer">Software Developer</option>
                <option value="Accountant">Accountant</option>
                <option value="อื่นๆ">{t('form.positionOther')}</option>
              </select>

            </label>
          </div>
        </div>
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={labelStyle}>
              {t('form.startDate')}
              <input
                style={inputStyle}
                type="date"
                name="start"
                value={formData.start}
                onChange={handleChange}
                required
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={labelStyle}>
              {t('form.endDate')}
              <input
                style={inputStyle}
                type="date"
                name="finish"
                value={formData.finish}
                onChange={handleChange}
                required
              />
            </label>
          </div>
        </div>
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={labelStyle}>
              {t('form.phone')}
              <input
                style={inputStyle}
                type="text"
                name="phone"
                value={formData.phone}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, "");
                  if (value.length > 10) value = value.slice(0, 10);
                  if (value.length > 6) value = value.replace(/(\d{3})(\d{3})(\d{0,4})/, "$1-$2-$3");
                  else if (value.length > 3) value = value.replace(/(\d{3})(\d{0,3})/, "$1-$2");
                  setFormData({ ...formData, phone: value });
                }}
                required
                placeholder="085-xxx-xxxx"
                maxLength={12}
              />
            </label>
          </div>

          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={labelStyle}>
              {t('form.education')}
              <select
                style={inputStyle}
                type="text"
                name="education"
                value={formData.education}
                onChange={handleChange}
                required
              >
                <option value="" disabled>{t('form.educationPh')}</option>
                <option value="ปวช.">{t('form.eduVocCert')}</option>
                <option value="ปวส.">{t('form.eduVocDiploma')}</option>
                <option value="ปริญญาตรี">{t('form.eduBachelor')}</option>
                <option value="ปริญญาโท">{t('form.eduMaster')}</option>
                <option value="ปริญญาเอก">{t('form.eduDoctorate')}</option>
                <option value="อื่นๆ">{t('form.eduOther')}</option>
              </select>
            </label>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ flex: 1, minWidth: 220, ...(shakeResume ? shakeAnimation : {}) }}>
            <label style={{ ...labelStyle, color: showResumeError ? '#ff0000' : '#222' }}>
              {t('form.resume')}
              <div
                onDragEnter={handleDragResume}
                onDragOver={handleDragResume}
                onDragLeave={handleDragResume}
                onDrop={handleDropResume}
                style={{
                  border: showResumeError ? '2px solid #ff0000' : (dragActiveResume ? '2px solid #1a73e8' : '2px dashed #b0b0b0'),
                  borderRadius: 8,
                  padding: 24,
                  textAlign: 'center',
                  background: dragActiveResume ? '#e3f0fd' : '#fafbfc',
                  cursor: 'pointer',
                  marginTop: 6,
                  marginBottom: 8,
                  color: showResumeError ? '#ff0000' : '#222',
                  transition: 'border 0.2s, background 0.2s',
                }}
              >
                {resumeFile ? (
                  <span>{t('form.fileSelected')} {resumeFile.name}</span>
                ) : (
                  <span>{t('form.resumeDrop')}</span>
                )}
                <input
                  ref={resumeInputRef}
                  style={{ display: 'none' }}
                  type="file"
                  name="resume"
                  onChange={handleResumeFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
              </div>
              {showResumeError && (
                <div style={{ color: '#ff0000', fontSize: '0.9rem', marginTop: '4px' }}>
                  {resumeErrorMessage || t('form.missingResume')}
                </div>
              )}
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 220, ...(shakeTranscript ? shakeAnimation : {}) }}>
            <label style={{ ...labelStyle, color: showTranscriptError ? '#ff0000' : '#222' }}>
              {t('form.transcript')}
              <div
                onDragEnter={handleDragTranscript}
                onDragOver={handleDragTranscript}
                onDragLeave={handleDragTranscript}
                onDrop={handleDropTranscript}
                style={{
                  border: showTranscriptError ? '2px solid #ff0000' : (dragActiveTranscript ? '2px solid #1a73e8' : '2px dashed #b0b0b0'),
                  borderRadius: 8,
                  padding: 24,
                  textAlign: 'center',
                  background: dragActiveTranscript ? '#e3f0fd' : '#fafbfc',
                  cursor: 'pointer',
                  marginTop: 6,
                  marginBottom: 8,
                  color: showTranscriptError ? '#ff0000' : '#222',
                  transition: 'border 0.2s, background 0.2s',
                }}
              >
                {transcriptFile ? (
                  <span>{t('form.fileSelected')} {transcriptFile.name}</span>
                ) : (
                  <span>{t('form.transcriptDrop')}</span>
                )}
                <input
                  ref={transcriptInputRef}
                  style={{ display: 'none' }}
                  type="file"
                  name="transcript"
                  onChange={handleTranscriptFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
              </div>
              {showTranscriptError && (
                <div style={{ color: '#ff0000', fontSize: '0.9rem', marginTop: '4px' }}>
                  {transcriptErrorMessage || t('form.missingTranscript')}
                </div>
              )}
            </label>
          </div>
        </div>
        <label style={labelStyle}>
          {t('form.additionalDetails')}
          <div
            style={{
              position: "relative",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              padding: "8px",
              background: "#fafbfc",
              minHeight: "120px",
            }}
            onDrop={(e) => {
              e.preventDefault();
              const files = Array.from(e.dataTransfer.files);
              const total = (formData.extraFiles?.length || 0) + files.length;
              if (total > 15) {
                alert("สามารถอัปโหลดได้สูงสุด 15 ไฟล์");
                return;
              }
              setFormData({
                ...formData,
                extraFiles: [...(formData.extraFiles || []), ...files],
              });
            }}
            onDragOver={(e) => e.preventDefault()}
          >
            {/* 🔹 กล่องข้อความ */}
            <textarea
              style={{
                width: "100%",
                padding: "8px",
                border: "none",
                outline: "none",
                resize: "none",
                background: "transparent",
                fontFamily: "inherit",
                minHeight: "80px",
              }}
              name="desc"
              value={formData.desc}
              onChange={handleChange}
              rows={3}
              placeholder={t('form.additionalDetailsPh')}
            />

            {/* 🔹 พื้นที่แสดงไฟล์ (อยู่ด้านในกรอบเดียวกัน) */}
            {formData.extraFiles && formData.extraFiles.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "8px",
                  marginTop: "8px",
                  overflowX: "auto",
                  alignItems: "center",
                  padding: "4px 0",
                  scrollbarWidth: "thin",
                }}
              >
                {formData.extraFiles.map((file, index) => (
                  <div
                    key={index}
                    onClick={() => handlePreview(file)}
                    style={{
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      background: "#fafbfc",
                      cursor: "pointer",
                      padding: "4px 10px",
                      minWidth: "160px",
                      maxWidth: "180px",
                      height: "42px",
                      transition: "all 0.2s ease-in-out",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#e8f0fe")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#fafbfc")
                    }
                  >
                    {/* ไอคอนหรือรูปตัวอย่าง */}
                    {file.type.startsWith("image/") ? (
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "4px",
                          objectFit: "cover",
                          border: "1px solid #ccc",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "28px",
                          height: "28px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          fontSize: "1.2rem",
                          background: "#f2f2f2",
                          borderRadius: "4px",
                        }}
                      >
                        📄
                      </div>
                    )}

                    {/* ชื่อไฟล์ด้านขวา */}
                    <div
                      style={{
                        flex: 1,
                        fontSize: "0.8rem",
                        color: "#444",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={file.name}
                    >
                      {file.name.length > 20
                        ? file.name.slice(0, 20) + "..."
                        : file.name}
                    </div>

                    {/* ปุ่มลบไฟล์ */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const updated = [...formData.extraFiles];
                        updated.splice(index, 1);
                        setFormData({ ...formData, extraFiles: updated });
                      }}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#e53935",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ปุ่มอัปโหลดทางเลือก */}
          <div
            onClick={() => document.getElementById("extraFileInput").click()}
            style={{
              display: "inline-block",
              border: "1.5px dashed #b0b0b0",
              borderRadius: "8px",
              padding: "8px 16px",
              textAlign: "center",
              background: "#fafbfc",
              color: "#555",
              cursor: "pointer",
              fontSize: "0.9rem",
              marginTop: "8px",
              marginLeft: "4px",
              transition: "all 0.2s ease-in-out",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.border = "1.5px dashed #1a73e8")}
            onMouseLeave={(e) => (e.currentTarget.style.border = "1.5px dashed #b0b0b0")}
          >
            {t('form.attachExtra')}
            <input
              id="extraFileInput"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
              style={{ display: "none" }}
              onChange={(e) => {
                const newFiles = Array.from(e.target.files || []);
                const existing = formData.extraFiles || [];
                const combined = [...existing, ...newFiles];

                // ถ้าอยากจำกัดจำนวน (เช่น 15)
                const limited = combined.slice(0, 15);

                setFormData({
                  ...formData,
                  extraFiles: limited,
                });

                // รีเซ็ตค่า input เพื่อให้เลือกไฟล์เดิมซ้ำได้
                e.target.value = null;
              }}
            />
          </div>

          {/* แสดงจำนวนไฟล์ที่เลือกไว้ (เล็กๆ ใต้ปุ่ม) */}
          {formData.extraFiles && formData.extraFiles.length > 0 && (
            <div style={{ fontSize: "0.8rem", color: "#777", marginTop: "4px" }}>
              {t('form.filesCountPrefix')}{formData.extraFiles.length}{t('form.filesCountSuffix')}
            </div>
          )}
        </label>

        <button type="submit" style={buttonStyle}>{t('form.submit')}</button>
      </form>
    </div>
  );
}

export default Form;